import fetchWithCredentials from "apiClient";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { Alert } from "types/Alert";

interface AlertsContextType {
  alerts: Alert[];
  loading: boolean;
  markAlertsAsRead: (studentId: number) => Promise<void>;
  refreshAlerts: () => Promise<void>;
  setAlerts: React.Dispatch<React.SetStateAction<Alert[]>>;
}

const AlertsContext = createContext<AlertsContextType | undefined>(undefined);

export const AlertsProvider: React.FC<{
  teacherId: number;
  children: React.ReactNode;
}> = ({ teacherId, children }) => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchWithCredentials(`/api/teacher/alerts/`);
      setAlerts(data);
    } catch (err) {
      console.error("Failed to fetch alerts", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const markAlertsAsRead = async (studentId: number) => {
    if (!alerts || alerts.length === 0) return;

    try {
      await fetchWithCredentials(
        `/api/teacher/alerts/${studentId}/mark-as-read/`,
        {
          method: "POST",
        }
      );
      setAlerts((prev) => prev.filter((a) => a.studentId !== studentId));
    } catch (err) {
      console.error("Failed to mark alerts as read", err);
    }
  };

  useEffect(() => {
    fetchAlerts();
  }, [teacherId, fetchAlerts]);

  return (
    <AlertsContext.Provider
      value={{
        alerts,
        loading,
        markAlertsAsRead,
        refreshAlerts: fetchAlerts,
        setAlerts,
      }}
    >
      {children}
    </AlertsContext.Provider>
  );
};

export const useAlerts = () => {
  const ctx = useContext(AlertsContext);
  if (!ctx) {
    throw new Error("useAlerts must be used within an AlertsProvider");
  }
  return ctx;
};
